import {PropsWithChildren, useEffect, useMemo, useState} from "react";
import {useScopedCollection} from "./useScopedCollection";
import {collection, DocumentData, documentId, getDocs, query, QueryConstraint, where} from 'firebase/firestore';
import {appFirestore} from "../index";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {firebaseDateToDate} from "../app/tools";
import {selectCalendar, setAllEvents, setEvents} from "../features/AppDataSlice";
import {RREventsType, RREventType} from "../app/types/RREventType";
import {RREventStatusCurrent, RREventStatusesType} from "../app/types/RREventStatusType";
import {selectDepartment} from "../features/AppStateSlice";

const getFirestoreIndexUrl = (errorMessage: string): string | null => {
    const match = errorMessage.match(/https:\/\/console\.firebase\.google\.com\/\S+/);
    return match?.[0] || null;
};

const handleFirestoreIndexUrl = (indexUrl: string): void => {
    console.log("FirebaseEventsLoader index URL (full):", indexUrl);

    if (typeof window !== "undefined") {
        window.open(indexUrl, "_blank", "noopener,noreferrer");
    }

    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(indexUrl).catch(() => {
            console.warn("FirebaseEventsLoader: failed to copy index URL to clipboard.");
        });
    }
};

export const eventDataConverter = (value: DocumentData): RREventType => {
    return {
        id: value.id,
        ref: value.ref,
        clientEmail: value.get("clientEmail"),
        clientName: value.get("clientName"),
        clientPhone: value.get("clientPhone"),
        clientSpoc: value.get("clientSpoc"),
        eventCategory: value.get("eventCategory"),
        eventNumber: value.get("eventNumber"),
        eventName: value.get("eventName"),
        eventShortName: value.get("eventShortName"),
        place: value.get("place"),
        eventStatus: value.get("eventStatus"),
        eventColor: value.get("eventColor") ?? "",
        planningRevision: value.get("planningRevision") || 0,
        firstDate: firebaseDateToDate(value.get("firstDate")) || new Date(),
        lastDate: firebaseDateToDate(value.get("lastDate")),
        face: value.get("face") || "NOT SET",
        notes: value.get("notes") || "",
        isDeleted: value.get("isDeleted"),
        isMontage: value.get("isMontage") || false,
        isCatering: value.get("isCatering") || false,
        isStore: value.get("isStore") || false,
    } as RREventType;
}

export const eventsDataConverter = (values: DocumentData[]): RREventsType => {
    if(!values) return [];

    return values.map(
        value => eventDataConverter(value)
    )
}

export interface Props{
    withStatus: RREventStatusesType;
}

const EVENT_ID_CHUNK_SIZE = 10;

function mergeEvents(...groups: RREventsType[]): RREventsType {
    const uniqueEvents = new Map<string, RREventType>();

    groups.forEach(group => {
        group.forEach(event => {
            if (event.id) {
                uniqueEvents.set(event.id, event);
            }
        });
    });

    return Array.from(uniqueEvents.values());
}

export const FirebaseEventsLoader: React.FC<PropsWithChildren<Props>> = (props) => {
    const dispatch = useAppDispatch();
    const department = useAppSelector(selectDepartment);
    const calendar = useAppSelector(selectCalendar);
    const [calendarEvents, setCalendarEvents] = useState<RREventsType>([]);
    const periodLimitDate = useMemo(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 14);
        return date;
    }, []);

    const constraints = useMemo<QueryConstraint[]>(() => {
        const next: QueryConstraint[] = [
            where("isDeleted", "==", false),
            where("eventStatus", "in", RREventStatusCurrent),
            where("firstDate", ">=", periodLimitDate),
        ];
        if (department === "catering") next.push(where("isCatering", "==", true));
        if (department === "montage") next.push(where("isMontage", "==", true));
        if (department === "store") next.push(where("isStore", "==", true));
        return next;
    }, [department, periodLimitDate]);

    // const eventIds = new Set<string>();
    // eventIds.add("WAREHOUSE");
    // calendarData.data.forEach(
    //     value => value.eventId && eventIds.add(value.eventId)
    // );
    // constraints.push(where("__name__", "in", Array.from(eventIds.values())));

    const eventsQuery = useMemo(
        () => query(collection(appFirestore, "events"), ...constraints),
        [constraints]
    );
    const {data, loading, error, subscription} = useScopedCollection(
        eventsQuery, `${department}:${periodLimitDate.getTime()}`
    );
    const baseEvents = useMemo(
        () => (data ? eventsDataConverter(data.docs) : []),
        [data]
    );
    const calendarEventIds = useMemo(() => {
        const eventIds = new Set<string>();
        eventIds.add("WAREHOUSE"); // Always load the WAREHOUSE event regardless of visible date range

        calendar.data.forEach(item => {
            if (item.department === department && item.eventId) {
                eventIds.add(item.eventId);
            }
        });

        return Array.from(eventIds.values());
    }, [calendar.data, department]);

    useEffect(() => {
        if (error) {
            console.error("FirebaseEventsLoader:", error);
            const indexUrl = getFirestoreIndexUrl(error.message);
            if (indexUrl) {
                handleFirestoreIndexUrl(indexUrl);
            }
        }
    }, [error]);

    useEffect(() => {
        const loadedEventIds = new Set(baseEvents.map(event => event.id).filter((eventId): eventId is string => !!eventId));
        const missingEventIds = calendarEventIds.filter(eventId => !loadedEventIds.has(eventId));

        if (missingEventIds.length === 0) {
            setCalendarEvents([]);
            return;
        }

        let isCancelled = false;

        const loadCalendarEvents = async () => {
            const eventDocs: DocumentData[] = [];

            for (let index = 0; index < missingEventIds.length; index += EVENT_ID_CHUNK_SIZE) {
                const eventIdsChunk = missingEventIds.slice(index, index + EVENT_ID_CHUNK_SIZE);
                const eventsSnapshot = await getDocs(
                    query(
                        collection(appFirestore, "events"),
                        where(documentId(), "in", eventIdsChunk)
                    )
                );

                eventDocs.push(...eventsSnapshot.docs);
            }

            if (!isCancelled) {
                setCalendarEvents(eventsDataConverter(eventDocs));
            }
        };

        loadCalendarEvents().catch(loadError => {
            console.error("FirebaseEventsLoader calendar events:", loadError);
            if (!isCancelled) {
                setCalendarEvents([]);
            }
        });

        return () => {
            isCancelled = true;
        };
    }, [baseEvents, calendarEventIds]);

    const mergedEvents = useMemo(
        () => mergeEvents(baseEvents, calendarEvents),
        [baseEvents, calendarEvents]
    );

    useEffect(() => {
        dispatch(setEvents(
            {
                loading: loading,
                error: error?.message,
                data: mergedEvents,
            }
        ));
    }, [dispatch, loading, error, mergedEvents]);

    useEffect(() => {
        dispatch(setAllEvents(
            {
                loading: loading,
                error: error?.message,
                data: mergedEvents,
            }
        ));
    }, [dispatch, loading, error, mergedEvents]);

    return <>{subscription}{props.children}</>;
}
